import React, { ReactNode } from 'react';
import { useFeature, useFeatures } from '../../hooks/useFeatures';
import { FeatureCode } from '../../common/constants/featureCodes';
import Button, { IButtonProps } from '../bootstrap/Button';

interface FeatureButtonProps extends Omit<IButtonProps, 'onClick'> {
	/** The feature code to check */
	featureCode: FeatureCode | string;
	/** Button content */
	children: ReactNode;
	/** Action to execute when button is clicked and feature is available */
	onAction: () => void | Promise<void>;
	/** Callback when feature is blocked (not available or limit reached) */
	onBlocked?: (reason: 'disabled' | 'limit_reached') => void;
	/** Automatically increment usage count on success (default: false) */
	trackUsage?: boolean;
	/** Show lock icon when disabled (default: true) */
	showLockIcon?: boolean;
}

/**
 * Button that checks feature availability before executing an action.
 * Wraps the project's Button component with feature gating logic.
 *
 * @example
 * ```tsx
 * <FeatureButton
 *   featureCode={FeatureCodes.ORGANIZATION_SEND_MESSAGES}
 *   onAction={handleSendMessage}
 *   trackUsage
 *   color="primary"
 * >
 *   Send Message
 * </FeatureButton>
 * ```
 */
const FeatureButton: React.FC<FeatureButtonProps> = ({
	featureCode,
	children,
	onAction,
	onBlocked,
	trackUsage = false,
	showLockIcon = true,
	isDisable,
	...buttonProps
}) => {
	const { canUse, checkResult, feature } = useFeature(featureCode);
	const { incrementLocalUsage } = useFeatures();

	// Only disable if explicitly set via isDisable prop, not for feature blocking
	// This allows the button to be clicked so onBlocked can redirect
	const isButtonDisabled = isDisable ?? false;

	const handleClick = async () => {
		if (!canUse) {
			onBlocked?.(checkResult.reason ?? 'disabled');
			return;
		}

		await onAction();

		// Optimistically increment usage on success
		if (trackUsage && feature?.type === 'limit') {
			incrementLocalUsage(featureCode);
		}
	};

	return (
		<Button onClick={handleClick} isDisable={isButtonDisabled} {...buttonProps}>
			{!canUse && showLockIcon && <span className='me-1'>🔒</span>}
			{children}
		</Button>
	);
};

export default FeatureButton;
